import BaseEntity from "../../core/entity/BaseEntity";
import Entity from "../../core/entity/Entity";
import { MasterComposer, MusicalComposer } from "./Composers/MasterComposer";
import { Instrument } from "./Instruments/Instrument";
import { MusicalNote } from "./MusicalNote";

/**
 * Entity that manages the lifecycle and scheduling of an instrument.
 *
 * Separates the event handling and composer integration from the pure
 * synthesis logic in the Instrument class. This allows instruments to be
 * tested independently and composed more easily.
 *
 * Architecture:
 * - Handles beat events from the game's event system
 * - Gets musical context from MasterComposer
 * - Uses MusicalComposer to generate notes
 * - Schedules notes for playback on the owned Instrument
 * - Exposes instrument parameters for UI control
 */
export class InstrumentPlayer extends BaseEntity implements Entity {
  pausable = false; // Keep music playing during pause

  private composer: MusicalComposer;
  private masterComposer: MasterComposer;
  private instrument: Instrument;

  constructor(
    composer: MusicalComposer,
    masterComposer: MasterComposer,
    instrument: Instrument,
  ) {
    super();
    this.composer = composer;
    this.masterComposer = masterComposer;
    this.instrument = instrument;
  }

  /**
   * Get the underlying instrument for direct access.
   */
  getInstrument(): Instrument {
    return this.instrument;
  }

  /**
   * Get the composer for visualization purposes.
   */
  getComposer(): MusicalComposer {
    return this.composer;
  }

  /**
   * Handle beat scheduling event - generate and schedule notes for the upcoming beat.
   * Called automatically by the game's event system when TempoClock dispatches scheduleBeat.
   * Uses lookahead timing to ensure audio events are scheduled before they need to play.
   */
  onScheduleBeat(eventData: {
    beatNumber: number;
    measureNumber: number;
    beatInMeasure: number;
    beatStartTime: number;
  }) {
    const { beatNumber, beatStartTime } = eventData;

    // Get complete musical context from master composer
    const context = this.masterComposer.getMusicalContext(beatNumber);

    // Generate notes using structured composer
    const notes = this.composer.generateNotesForBeat(context);

    // Schedule all generated notes with precise timing
    for (const note of notes) {
      this.scheduleNoteWithTime(note, beatStartTime);
    }
  }

  /**
   * Schedule a musical note for playback using precise beat start time.
   */
  private scheduleNoteWithTime(note: MusicalNote, beatStartTime: number) {
    if (!this.game?.audio) return;

    const tempoClock = this.game.entities.getById("tempoClock") as any;
    if (!tempoClock?.beatsToSeconds) return;

    // Calculate precise start time relative to beat start
    const beatOffset = note.startBeat - Math.floor(note.startBeat);
    const offsetTime = tempoClock.beatsToSeconds(beatOffset);
    const startTime = beatStartTime + offsetTime;

    const duration = tempoClock.beatsToSeconds(note.durationBeats);

    // Delegate synthesis to the instrument
    this.instrument.playNote(note, startTime, duration);
  }

  /**
   * Schedule a musical note for playback on the instrument (legacy method).
   */
  private scheduleNote(note: MusicalNote) {
    if (!this.game?.audio) return;

    // Convert beat time to absolute audio context time
    const tempoClock = this.game.entities.getById("tempoClock") as any;
    if (!tempoClock?.beatToTime) return;

    const startTime = tempoClock.beatToTime(note.startBeat);
    const duration = tempoClock.beatsToSeconds(note.durationBeats);

    // Delegate synthesis to the instrument
    this.instrument.playNote(note, startTime, duration);
  }

  /**
   * Get all valid MIDI notes that this instrument can play.
   */
  getValidNotes(): number[] {
    return this.instrument.getValidNotes();
  }

  /**
   * Get a display name for this instrument.
   */
  getDisplayName(): string {
    return this.instrument.getDisplayName();
  }

  /**
   * Set instrument volume (0-1).
   */
  setVolume(volume: number) {
    this.instrument.setVolume(volume);
  }

  getVolume(): number {
    return this.instrument.getVolume();
  }

  /**
   * Mute or unmute the instrument.
   */
  setMuted(muted: boolean) {
    this.instrument.setMuted(muted);
  }

  /**
   * Check if the instrument is muted.
   */
  isMuted(): boolean {
    return this.instrument.isMuted();
  }

  /**
   * Toggle mute state.
   */
  toggleMute(): void {
    this.instrument.toggleMute();
  }

  /**
   * Get the monitor output for this instrument.
   */
  get monitor(): AudioNode {
    return this.instrument.monitor;
  }

  /**
   * Get all parameters for this instrument.
   */
  getParameters() {
    return this.instrument.getParameters();
  }

  /**
   * Find a parameter by key.
   */
  getParameter(key: string) {
    return this.instrument.getParameter(key);
  }

  /**
   * Set a parameter value by key.
   */
  setParameter(key: string, value: number): void {
    this.instrument.setParameter(key, value);
  }

  /**
   * Reset all parameters to their default values.
   */
  resetParametersToDefaults(): void {
    this.instrument.resetParametersToDefaults();
  }

  /**
   * Compatibility methods for TrackList component.
   */
  playNextBeat(): void {
    // Not needed in structured system - handled by event system
  }

  /**
   * Cleanup when entity is destroyed.
   */
  onDestroy(): void {
    this.instrument.onDestroy();
  }
}
